/* AppSidebar — concrete sidebar used by every route (Phase A1).
 *
 * Composes the v2 <Sidebar> primitive with:
 *   - items config (5 routes, admin-gated)
 *   - active item derived from useLocation()
 *   - collapse state persisted to localStorage
 *   - viewer-aware: admin items only visible when viewer.isAdmin
 *   - bilingual labels via useLang()
 *
 * Phase A1 scope: persistent navigation only. No groups, no bottom
 * controls (theme/lang/viewer). Topbar wiring + per-view chrome
 * removal land in Phase A2.
 */
import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Sidebar from "./Sidebar";
import { useLang } from "../../lib/lang";
import { useViewer } from "../../lib/data/viewer";

const STORAGE_KEY_COLLAPSED = "forecast_sidebar_collapsed";

// Small inline-SVG icons. Vendored Lucide-style. Inherit currentColor so
// dark/light parity is automatic.
const IconForecast = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="2,12 6,7 9,10 14,4" />
    <polyline points="10,4 14,4 14,8" />
  </svg>
);
const IconUser = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="8" cy="5.5" r="2.5" />
    <path d="M3 13.5c0-2.5 2.2-4 5-4s5 1.5 5 4" />
  </svg>
);
const IconUsers = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="6" cy="6" r="2.2" />
    <path d="M2 13c0-2 1.8-3.4 4-3.4S10 11 10 13" />
    <circle cx="11.5" cy="5.5" r="1.8" />
    <path d="M10.5 13c0-1.6 1.4-2.8 3-2.8" />
  </svg>
);
const IconLog = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="2.5" width="10" height="11" rx="1.5" />
    <line x1="5.5" y1="5.5" x2="10.5" y2="5.5" />
    <line x1="5.5" y1="8" x2="10.5" y2="8" />
    <line x1="5.5" y1="10.5" x2="8.5" y2="10.5" />
  </svg>
);
const IconMap = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M1.5 4 6 2.5l4 1.5L14.5 2.5v9.5L10 13.5l-4-1.5L1.5 13.5z" />
    <line x1="6" y1="2.5" x2="6" y2="12" />
    <line x1="10" y1="4" x2="10" y2="13.5" />
  </svg>
);

function readCollapsed(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return localStorage.getItem(STORAGE_KEY_COLLAPSED) === "1";
  } catch {
    return false;
  }
}

function writeCollapsed(v: boolean) {
  try { localStorage.setItem(STORAGE_KEY_COLLAPSED, v ? "1" : "0"); } catch {}
}

export default function AppSidebar() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const lang = useLang();
  const { viewer } = useViewer();
  const isAdmin = !!viewer?.isAdmin;

  const [collapsed, setCollapsedState] = React.useState<boolean>(readCollapsed);
  const setCollapsed = React.useCallback((next: boolean) => {
    setCollapsedState(next);
    writeCollapsed(next);
  }, []);

  // Item definitions. id matches the primary route; for the admin items,
  // gating is by viewer.isAdmin so non-admins never see them.
  const items = React.useMemo(() => {
    const list = [
      {
        id: "forecast",
        label: lang === "it" ? "Forecast Italia" : "Forecast Italy",
        icon: <IconForecast />,
        href: "/forecast/italy",
      },
      {
        id: "me",
        label: lang === "it" ? "Il mio profilo" : "My profile",
        icon: <IconUser />,
        href: "/me",
      },
    ];
    if (isAdmin) {
      list.push(
        {
          id: "admin-users",
          label: lang === "it" ? "Utenti" : "Users",
          icon: <IconUsers />,
          href: "/admin/users",
        },
        {
          id: "admin-audit",
          label: lang === "it" ? "Registro modifiche" : "Audit log",
          icon: <IconLog />,
          href: "/admin/audit",
        }
      );
    }
    list.push({
      id: "map",
      label: lang === "it" ? "Mappa piattaforma" : "Platform map",
      icon: <IconMap />,
      href: "/map",
    });
    return list;
  }, [lang, isAdmin]);

  // Derive active item from current pathname. Match by prefix so
  // /u/<slug> still highlights "me" (profile), /admin/audit highlights
  // the audit item, etc.
  const currentId = React.useMemo(() => {
    if (pathname.startsWith("/forecast")) return "forecast";
    if (pathname.startsWith("/market")) return "forecast"; // market = forecast variant for now
    if (pathname === "/me" || pathname.startsWith("/u/")) return "me";
    if (pathname.startsWith("/admin/users")) return "admin-users";
    if (pathname.startsWith("/admin/audit")) return "admin-audit";
    if (pathname.startsWith("/map")) return "map";
    return null;
  }, [pathname]);

  const onNavigate = React.useCallback(
    (id: string) => {
      const item = items.find((x) => x.id === id);
      if (item?.href) navigate(item.href);
    },
    [items, navigate]
  );

  // Brand block — shown when expanded. Logo is the wordmark/title only
  // (image lives in the legacy header for now; Phase A2 may move it here).
  const brand = (
    <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
      <span
        style={{
          fontWeight: "var(--fw-semibold)",
          fontSize: "var(--fs-body)",
          color: "var(--text-primary)",
          whiteSpace: "nowrap",
        }}
      >
        fonio · Sales
      </span>
    </div>
  );

  return (
    <Sidebar
      items={items as any}
      currentId={currentId as any}
      onNavigate={onNavigate}
      collapsed={collapsed}
      onToggleCollapsed={() => setCollapsed(!collapsed)}
      brand={brand}
    />
  );
}
